import { PageType } from '../../hooks/UrlManager/types';
import { Dictionary } from '../../../../types/globals';
import { GenericShopApiResponse } from '../../../common/apiAdapters/types';
import { NavigationBaseData } from '../../components/Generic/NavigationItem';
import { ProductImageTag } from '../productdetail/types';

export interface ProductResponse {
    meta: {
        /* eslint-disable @typescript-eslint/camelcase, camelcase */
        sku: string;
        image_url: string;
        name: string;
        alt_tag: string;
        brand: string;
        black_friday_top_deal: boolean;
        cyber_monday_top_deal: boolean;
        pdp_url: string;
        soldout?: boolean;
        quantity_badge_number?: number;
        /* eslint-disable @typescript-eslint/camelcase, camelcase */
    };
    simples: {
        [key: string]: ProductSimpleItem;
    };
}

export interface ApiResponseAppliedFilters {
    [key: string]: {
        type: string;
        values: Array<string> | Object;
    };
}

export interface ProductsResponseData {
    filters?: Array<ProductFilters>;
    sorting?: Array<ProductSorting>;
    didYouMean?: any;
    totalCount?: number;
    products?: Array<ProductResponse>;
    crossCategoryNavigation?: CrossCategoryNavigationResponse;
    appliedSorting?: AppliedSorting;
    appliedFilters?: ApiResponseAppliedFilters;
}

export type ProductsResponse = GenericShopApiResponse<ProductsResponseData>;

export type SortingOrderType = 'asc' | 'desc';

export interface AppliedSorting {
    name: string;
    order: SortingOrderType;
}

export interface ProductSimpleItem {
    meta: {
        /* eslint-disable @typescript-eslint/camelcase, camelcase */
        price: number;
        original_price: number;
        available: boolean;
        is_visible?: boolean;
        image_url: string;
        alt_tag: string;
        filter_size?: string;
        delivery_time?: number;
        installments_monthly_price?: number;
        installments_term?: number;
        /* eslint-disable @typescript-eslint/camelcase, camelcase */
    };
}

export enum WishListTrackingAction {
    ADD = 'add',
    REMOVE = 'remove',
}
export type WishlistTrackingFn = (action: WishListTrackingAction) => void;
export interface ProductInterface {
    sku: string;
    simpleSku: string;
    baseImage: string;
    name: string;
    altTag: string;
    brand: string;
    blackFriday: boolean;
    cyberMonday: boolean;
    price: string;
    originalPrice: string;
    link: string;
    simplesCount: number;
    quantityBadge: number;
    position: number;
    soldOut: boolean;
    clickTrackingFn?: () => void;
    wishListTrackingFn?: WishlistTrackingFn;
    impressionTrackingFn?: () => void;
    productImage: {
        path: string;
        seoUrl: string;
        tag: ProductImageTag;
    };
}

export interface CrossCategoryNavigationCategoryResponse {
    id: string;
    name: string;
    image: {};
    isSelected: boolean;
    links: any[];
}

export interface CrossCategoryNavigationLinkResponse {
    rel: string;
    href: string;
}

export interface CrossCategoryNavigationResponse {
    categories: CrossCategoryNavigationCategoryResponse[];
    parentCategory: { isRoot: boolean; links: CrossCategoryNavigationLinkResponse[] };
}

export interface CrossCategoryNavigationParentCategory {
    isRoot: boolean;
    link: string;
}

export interface CrossCategoryNavigation {
    categories: NavigationBaseData[];
    parentCategory: CrossCategoryNavigationParentCategory;
}

export interface ProductsListLinks {
    rel: string;
    href: string;
}

export enum SecondaryProductsIdentifier {
    ALL_PRODUCTS = 'allProducts',
    NEW_PRODUCTS = 'newProducts',
}

export interface BasicProductsInterface {
    products: ProductInterface[];
    isFetching: boolean;
    isFetchingComplete?: boolean;
    didInvalidate: boolean;
    error?: any;
}

export interface ProductsState extends BasicProductsInterface {
    didYouMean: ReducedDidYouMeanInterface[];
    filters: ProductFilters[];
    sorting: ProductSorting[];
    crossCategoryNavigation: CrossCategoryNavigation;
    pageTitle: string;
    cmsPageTitle: string;
    totalCount: number;
    identifier: string;
    links: ProductsListLinks[];
    pdpUrl: string;
}

export interface GenericProductsState {
    identifier: string;
    isFetching: boolean;
    isFetchingComplete?: boolean;
    didInvalidate: boolean;
    products: ProductInterface[];
    error?: any;
}

export interface SecondaryProductsState {
    products: { [identifier in SecondaryProductsIdentifier]: GenericProductsState };
}

export interface DidYouMeanInterface {
    title: string;
    links: LinkResponse[];
}

export interface ReducedDidYouMeanInterface {
    title: string;
    relLink: string;
}

export interface LinkResponse extends CrossCategoryNavigationLinkResponse {}

export interface SortingParameters {
    sort?: string;
    order?: 'desc' | 'asc';
}

export enum productFilterTypes {
    standard = 'standard',
    range = 'range',
    multiRange = 'multi-range',
}

export interface HydratedFilterValue {
    id_catalog_brand: string;
    name: string;
    url_key: string;
}

export enum pathFilterNames {
    colors = 'facet_col',
    materials = 'facet_mat_filter',
    brands = 'facet_brand_keys',
    categories = 'facet_category_keys',
    sizes = 'facet_generic_size',
    styles = 'facet_styles',
    pattern = 'facet_pattern',
    form = 'facet_form',
    pillowType = 'facet_filter_pillow_type',
}

export interface FiltersWithHydratedValues extends ProductFilters {
    hydratedValues: Dictionary<HydratedFilterValue>;
}

export interface BrandsFilters extends FiltersWithHydratedValues {}
export interface CategoryFilters extends FiltersWithHydratedValues {}

export const isBrandsFilter = (input: ProductFilters): input is BrandsFilters => input.name === pathFilterNames.brands;
export const isCategoriesFilter = (input: ProductFilters): input is CategoryFilters =>
    input.name === pathFilterNames.categories;

export interface ProductFilterLink {
    name: string;
    link: string;
}

export interface ProductFilters {
    name: string;
    displayName: string;
    type: productFilterTypes;
    values: { [key: string]: number };
    links?: ProductFilterLink[];
}

export interface ProductSorting extends AppliedSorting {
    displayName: string;
}

export interface NewProductFilterValues {
    name: string;
    value: string | string[];
}

export enum FilterType {
    notApplied = 'not-applied',
    query = 'query',
    materials = 'materials',
    colors = 'colors',
    brands = 'brands',
    styles = 'styles',
    categories = 'categories',
    sizes = 'sizes',
    pattern = 'pattern',
    form = 'form',
    pillowType = 'pillowType',
}

export interface AppliedFilter {
    type: FilterType;
    name: string;
    values: Array<string>;
}

export interface ListingPageUrlState {
    appliedFilters: AppliedFilter[];
    listingPageKey: string;
    listingPageType: PageType;
    sortingParams: SortingParameters;
    search?: string;
    strictSearch?: string;
    page: number;
}
export interface PriceFormatConfigOptions {
    countryCode: string;
    currency: string;
}
